import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { PackagingHeavyMetalsAuditStoredEntry } from '@/shared/hooks/api/packagingSuppliers'
import { PackagingAssessmentRunDetailSheet } from './PackagingAssessmentRunDetailSheet'
import { formatRunTimestamp } from './PackagingFieldCrossCheckRunResults'
import { PackagingHeavyMetalsAuditRunResults } from './PackagingHeavyMetalsAuditRunResults'
import {
  packagingHeavyMetalsAuditVerdictLabelKey,
  packagingItemHeavyMetalsAuditStatusLabelKey,
} from './packagingItemHeavyMetalsAuditFilter'
import type { PackagingItemHeavyMetalsAuditStatus } from '@/shared/hooks/api/packagingItemsOverview'
import { heavyMetalsAuditVerdictToOverviewStatus } from './packagingItemHeavyMetalsAuditOverviewClient'
import { isManualOverrideEntry } from './packagingAuditOverrideValidation'

type PackagingHeavyMetalsAuditHistoryTableProps = {
  history: PackagingHeavyMetalsAuditStoredEntry[]
  locale: string
}

function verdictBadgeVariant(
  status: PackagingItemHeavyMetalsAuditStatus
): 'success' | 'danger' | 'warning' | 'secondary' {
  if (status === 'passed') {
    return 'success'
  }
  if (status === 'flagged') {
    return 'warning'
  }
  if (status === 'failed') {
    return 'danger'
  }
  return 'secondary'
}

function HeavyMetalsAuditHistorySummary({
  entry,
}: {
  entry: PackagingHeavyMetalsAuditStoredEntry
}) {
  const { t } = useTranslation('packagingSuppliers')
  const status = heavyMetalsAuditVerdictToOverviewStatus(entry.verdict)

  return (
    <div className='flex flex-wrap items-center gap-2'>
      <Badge variant={verdictBadgeVariant(status)}>
        {t(packagingItemHeavyMetalsAuditStatusLabelKey(status))}
      </Badge>
      {isManualOverrideEntry(entry) ? (
        <span className='text-sm text-muted-foreground'>
          {entry.manualOverride?.reason ?? entry.reasoningEn}
        </span>
      ) : (
        <span className='text-sm text-muted-foreground'>
          {t(packagingHeavyMetalsAuditVerdictLabelKey(entry.verdict))}
        </span>
      )}
    </div>
  )
}

export function PackagingHeavyMetalsAuditHistoryTable({
  history,
  locale,
}: PackagingHeavyMetalsAuditHistoryTableProps) {
  const { t } = useTranslation('packagingSuppliers')
  const [selectedTimestamp, setSelectedTimestamp] = useState<string | null>(
    null
  )

  const selectedEntry = history.find(
    (entry) => entry.timestamp === selectedTimestamp
  )
  const selectedTitle =
    selectedEntry != null
      ? formatRunTimestamp(selectedEntry.timestamp, locale)
      : ''

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              {t('itemDetail.heavyMetalsAudit.history.columnRun')}
            </TableHead>
            <TableHead>
              {t('itemDetail.heavyMetalsAudit.history.columnSummary')}
            </TableHead>
            <TableHead className='w-[1%] text-end'>
              <span className='sr-only'>
                {t('itemDetail.heavyMetalsAudit.history.columnActions')}
              </span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {history.map((entry) => {
            const runLabel = formatRunTimestamp(entry.timestamp, locale)
            return (
              <TableRow key={entry.timestamp}>
                <TableCell className='font-medium'>
                  <div className='flex flex-wrap items-center gap-2'>
                    <span>{runLabel}</span>
                    {isManualOverrideEntry(entry) ? (
                      <Badge variant='outline'>
                        {t('itemDetail.manualOverride.tag')}
                      </Badge>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell>
                  <HeavyMetalsAuditHistorySummary entry={entry} />
                </TableCell>
                <TableCell className='text-end'>
                  <Button
                    type='button'
                    variant='link'
                    className='h-auto px-0'
                    onClick={() => {
                      setSelectedTimestamp(entry.timestamp)
                    }}
                  >
                    {t('itemDetail.heavyMetalsAudit.history.viewResults')}
                  </Button>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      <PackagingAssessmentRunDetailSheet
        open={selectedEntry != null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedTimestamp(null)
          }
        }}
        title={selectedTitle}
      >
        {selectedEntry != null ? (
          isManualOverrideEntry(selectedEntry) ? (
            <p className='text-sm text-muted-foreground whitespace-pre-wrap'>
              {selectedEntry.manualOverride?.reason ?? selectedEntry.reasoningEn}
            </p>
          ) : (
            <PackagingHeavyMetalsAuditRunResults result={selectedEntry} />
          )
        ) : null}
      </PackagingAssessmentRunDetailSheet>
    </>
  )
}
